import { format, parseISO } from "date-fns";
import { DateTime } from "luxon";

interface DATE_FORMAT_TYPE {
  long: string;
  /** Abbreviated month + day, no year, e.g. `Jul 2`. */
  dayMonth: string;
  short: string;
  shorts: string[];
}

type DATE_FORMATS_TYPE = Record<string, DATE_FORMAT_TYPE>;

export const ONE_DAY_MS = 24 * 60 * 60 * 1000;
export const DEFAULT_DATE_FORMAT = "MM/DD/YYYY";
export const ISO_DATE_FORMAT = "YYYY-MM-DD";
export const DATE_FORMATS: DATE_FORMATS_TYPE = {
  "MM/DD/YYYY": {
    long: "MMM d, yyyy",
    dayMonth: "MMM d",
    short: "MM/dd/yyyy",
    shorts: [
      "MM/dd/yyyy",
      "M/d/yyyy",
      "MM/d/yyyy",
      "M/dd/yyyy",
      "MM/dd/yy",
      "M/d/yy",
      "MM/d/yy",
      "M/dd/yy",
    ],
  },
  "DD/MM/YYYY": {
    long: "d MMM yyyy",
    dayMonth: "d MMM",
    short: "dd/MM/yyyy",
    shorts: [
      "dd/MM/yyyy",
      "d/M/yyyy",
      "d/MM/yyyy",
      "dd/M/yyyy",
      "dd/MM/yy",
      "d/M/yy",
      "d/MM/yy",
      "dd/M/yy",
    ],
  },
  "YYYY-MM-DD": {
    long: "yyyy-MM-dd HH:mm:ss",
    dayMonth: "MM-dd",
    short: "yyyy-MM-dd",
    shorts: [
      "yyyy-MM-dd",
      "yyyy-M-d",
      "yyyy-MM-d",
      "yyyy-M-dd",
      "yy-MM-dd",
      "yy-M-d",
      "yy-MM-d",
      "yy-M-dd",
    ],
  },
};

export const toStandardFormat = (
  maybeDate: Date | string,
  dateFormatOverride: string = DATE_FORMATS[DEFAULT_DATE_FORMAT].long,
): string => {
  try {
    const date = typeof maybeDate === "string" ? parseISO(maybeDate) : maybeDate;
    return format(date, dateFormatOverride);
  } catch {
    return "";
  }
};

/**
 * Formats a calendar date (e.g., payment date, invoice date, due date) without timezone conversion.
 * The date is stored in UTC in the database, but we want to display the date portion
 * as it was selected by the user, without converting to the user's local timezone.
 * This prevents the date from shifting by one day due to timezone differences.
 *
 * Use this for any date that represents a "calendar date" (a specific day on the calendar)
 * rather than a timestamp (a specific moment in time).
 *
 * @param dateString - The ISO date string to format (can be null or undefined)
 * @returns The formatted date string in "MMM d, yyyy" format, or null if input is null/undefined/invalid
 */
export const formatCalendarDate = (dateString: string | null | undefined): string | null => {
  if (!dateString) {
    return null;
  }

  try {
    // Parse the UTC date and format it using the date portion only
    // This avoids timezone conversion that would shift the date
    const dateTime = DateTime.fromISO(dateString, { zone: "UTC" });

    // Check if the DateTime is valid before formatting
    if (!dateTime.isValid) {
      return null;
    }

    return dateTime.toFormat("MMM d, yyyy");
  } catch {
    return null;
  }
};
